// Routes excluded from visual refresh theme (blocklist approach)
const EXCLUDED_ROUTES = [
    /^\/premier-agent(\/.*)?$/, // Matches /premier-agent/ and all sub-paths
];

/**
 * Safely parses pathname from asPath with error handling
 */
export function parsePathname(asPath) {
    try {
        return new URL(asPath, 'http://dummy.base').pathname;
    } catch (error) {
        return asPath || '/';
    }
}

const B2B_PATH_PREFIXES = [
    '/premier-agent',
    '/premieragent',
    '/new-construction-advertising',
    '/rentals-network',
    '/agents',
    '/pro',
    '/preferred',
    '/news',
    '/research',
];

// Matches whole path segments so /pro doesn't match /product-releases/
function matchesPathPrefix(asPath, prefixes) {
    const p = parsePathname(asPath)?.toLowerCase() || '';
    return prefixes.some(
        (prefix) => p === prefix || p.startsWith(`${prefix}/`)
    );
}

export function isB2BPath(asPath) {
    return matchesPathPrefix(asPath, B2B_PATH_PREFIXES);
}

export function shouldUseVisualRefreshTheme(pathname, query = {}) {
    // Normalize pathname once
    const p = pathname?.toLowerCase() || '';

    // Special case for preview - only use visual refresh if explicitly requested via query param
    if (p.includes('/preview/') && query?.useVisualRefresh !== undefined) {
        const useVisualRefreshParam = Array.isArray(query.useVisualRefresh)
            ? query.useVisualRefresh[0]
            : query.useVisualRefresh;
        // Handle both boolean and string values
        return (
            useVisualRefreshParam === true || useVisualRefreshParam === 'true'
        );
    }

    // Return true for all routes EXCEPT those in the blocklist
    return !EXCLUDED_ROUTES.some((regex) => regex.test(p));
}

// Pages rendered with the `zpro` theme (Zillow Pro, MOPS-31773). Exact pages
// only: sub-paths and every other /pro page keep `zillow`. Add a page here to
// opt it in. `/agents/*` is left out because next.config.js redirects it to
// `/pro/*` (agents-redirects.js), so those URLs never render.
const ZPRO_THEME_ROUTES = ['/pro/', '/pro/membership/'];

// Lowercases and adds a trailing slash, so `/pro`, `/PRO/` and `/pro/?x=1`
// all match `/pro/`.
function normalizeRoute(asPath) {
    const p = parsePathname(asPath)?.toLowerCase() || '/';
    return p.endsWith('/') ? p : `${p}/`;
}

export function isZproThemePath(asPath) {
    return ZPRO_THEME_ROUTES.includes(normalizeRoute(asPath));
}

/**
 * Panda theme (`data-panda-theme`) for a page. `zpro` builds on the Visual
 * Refresh `zillow` theme, so it only applies when visual refresh is on. With
 * it off (e.g. a preview with `?useVisualRefresh=false`) every path gets
 * `legacy-zillow`, matching the styled-components theme and page frame.
 *
 * Previews (`/pro/preview/<id>`) can't be matched to a slug, so previews of
 * the `zpro` pages render `zillow`.
 *
 * @param {string} pathname
 * @param {boolean} useVisualRefreshTheme result of shouldUseVisualRefreshTheme
 * @returns {'zpro' | 'zillow' | 'legacy-zillow'}
 */
export function getThemeName(pathname, useVisualRefreshTheme) {
    if (!useVisualRefreshTheme) {
        return 'legacy-zillow';
    }
    return isZproThemePath(pathname) ? 'zpro' : 'zillow';
}

const _zconfigs = {
    prod: {
        serverURLs: {
            MAX: 'https://mortgageapi.zillow.com',
        },
        partnerId: 'RD-WXDQSGN',
    },
    default: {
        serverURLs: {
            MAX: 'https://mortgageapi.qa.zillow.net',
        },
        partnerId: 'RD-GKZRBBL',
    },
};

export function getZmAPIConfig() {
    return _zconfigs[process.env.NEXT_PUBLIC_ENV] || _zconfigs.default;
}
